/**
 * React Query hooks for rower data management
 * Provides automatic caching, background updates, and optimized loading
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { rowerService } from '../services/users/rowerService';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';

/**
 * Signed URL for `rower-avatars` storage path (sidebar, settings, etc.).
 * Cached per path so navigating away and back does not recreate the URL or reload the image
 * until the signed URL is stale (aligned with ~1h token; we refresh before that).
 */
export function useRowerAvatarSignedUrl(avatarPath: string | null | undefined): string | null {
  const { data } = useQuery({
    queryKey: ['rower-avatar-signed-url', avatarPath],
    queryFn: async () => {
      if (!avatarPath) return null;
      const { data: signed, error } = await supabase.storage
        .from('rower-avatars')
        .createSignedUrl(avatarPath, 3600);
      if (error) throw error;
      return signed?.signedUrl ?? null;
    },
    enabled: !!avatarPath,
    staleTime: 50 * 60 * 1000,
    gcTime: 55 * 60 * 1000,
  });
  return data ?? null;
}

/**
 * Hook to fetch rower profile by user ID
 * Cached for 10 minutes, refetches in background
 */
export const useRowerProfile = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['rower-profile', user?.id],
    queryFn: () => {
      if (!user?.id) throw new Error('User not authenticated');
      return rowerService.getRowerProfileByUserId(user.id);
    },
    enabled: !!user?.id && !!user, // Disable if user is null/undefined
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Use cached data if available
  });
};

/**
 * Hook to fetch rower teams
 * Cached for 10 minutes
 */
export const useRowerTeams = (rowerProfileId: string | null) => {
  return useQuery({
    queryKey: ['rower-teams', rowerProfileId],
    queryFn: () => {
      if (!rowerProfileId) throw new Error('Rower profile ID required');
      return rowerService.getRowerTeams(rowerProfileId);
    },
    enabled: !!rowerProfileId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * Hook to fetch rower sessions
 * Cached for 5 minutes (sessions change more frequently)
 */
export const useRowerSessions = (rowerProfileId: string | null) => {
  return useQuery({
    queryKey: ['rower-sessions', rowerProfileId],
    queryFn: () => {
      if (!rowerProfileId) throw new Error('Rower profile ID required');
      return rowerService.getRowerSessions(rowerProfileId);
    },
    enabled: !!rowerProfileId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * Hook to search rowing clubs for profile dropdown
 */
export const useRowingClubsSearch = (query: string, enabled = true) => {
  return useQuery({
    queryKey: ['rowing-clubs-search', query],
    queryFn: () => rowerService.searchRowingClubs(query),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch a single rower session by session ID
 */
export const useRowerSession = (sessionId: string | null, rowerProfileId: string | null) => {
  return useQuery({
    queryKey: ['rower-session', sessionId, rowerProfileId],
    queryFn: () => {
      if (!sessionId || !rowerProfileId)
        throw new Error('Session ID and rower profile ID required');
      return rowerService.getRowerSession(sessionId, rowerProfileId);
    },
    enabled: !!sessionId && !!rowerProfileId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * Hook to fetch rower's position in a specific session
 */
export const useRowerPosition = (sessionId: string | null, rowerProfileId: string | null) => {
  return useQuery({
    queryKey: ['rower-position', sessionId, rowerProfileId],
    queryFn: () => {
      if (!sessionId || !rowerProfileId)
        throw new Error('Session ID and rower profile ID required');
      return rowerService.getRowerPositionInSession(sessionId, rowerProfileId);
    },
    enabled: !!sessionId && !!rowerProfileId,
    staleTime: 10 * 60 * 1000, // 10 minutes (position doesn't change)
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * Hook to invalidate all rower data cache
 * Useful after profile/session changes or when data needs to be refreshed
 */
export const useInvalidateRowerData = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['rower-profile', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['rower-teams'] });
    queryClient.invalidateQueries({ queryKey: ['rower-sessions'] });
    queryClient.invalidateQueries({ queryKey: ['rower-session'] });
    queryClient.invalidateQueries({ queryKey: ['rower-position'] });
  };
};
